import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Trophy, Download, Search, MessageCircle, Award, Crown } from "lucide-react";
import { Avatar } from "@/components/PhotoUpload";
import { whatsappLink } from "@/lib/whatsapp";
import { ClubWhatsApp } from "@/components/ClubWhatsApp";
import { ElectionPanel } from "@/components/ElectionPanel";
import { RecruitPanel } from "@/components/RecruitPanel";
import ClubAttendanceRates from "@/components/ClubAttendanceRates";
import { useAuth } from "@/context/AuthContext";

const CLUB_EMOJI = { Analistas: "🧠", Diplomatas: "🤝", Sentinelas: "🛡️", Exploradores: "🚀" };
const CLUB_COLOR = { Analistas: "#A855F7", Diplomatas: "#22C55E", Sentinelas: "#3B82F6", Exploradores: "#EAB308" };
const ROLE_COLOR = {
  Coopetidor: "#22C55E", Observador: "#737373", "Secretário": "#3B82F6",
  "Presidente do Clube": "#E50000", "Presidente da Liga": "#E50000", Staff: "#EAB308", Admin: "#EAB308",
};

export default function ClubMembers() {
  const { club } = useParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [tenure, setTenure] = useState("");
  const [sort, setSort] = useState("points");
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/composition/club/${encodeURIComponent(club)}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [club]);

  const roles = useMemo(() => [...new Set((data?.groups || []).flatMap((g) => g.people.map((p) => p.role)))], [data]);
  const groups = useMemo(() => (data?.groups || []).map((g) => ({
    ...g, people: sortPeople(g.people.filter((m) => matches(m, q, role, tenure)), sort),
  })), [data, q, role, tenure, sort]);
  const shown = groups.reduce((n, g) => n + g.people.length, 0);

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/composition/club/${encodeURIComponent(club)}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `membros_${club.toLowerCase()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const color = CLUB_COLOR[club] || "#fff";
  if (!data) return <div className="kw-overline">A carregar…</div>;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="club-members-page">
      <div>
        <Link to="/composition" className="kw-overline text-[10px] inline-flex items-center gap-1 hover:text-white mb-3"
          data-testid="club-members-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Composição
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter flex items-center gap-3">
          <span>{CLUB_EMOJI[club]}</span>
          <span style={{ color }}>{club}</span>
          <span className="text-[#737373] text-3xl" data-testid="club-members-total">{data.total}</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2">Todos os membros do clube, agrupados por perfil MBTI.</p>
      </div>
      {["admin", "staff"].includes(user?.role) && (
        <div className="grid xl:grid-cols-2 gap-4">
          <ElectionPanel club={club} kind="coopetidor" />
          <ElectionPanel club={club} kind="president" />
        </div>
      )}
      <ClubAttendanceRates club={club} />
      <RecruitPanel club={club} />
      <Toolbar {...{ q, setQ, role, setRole, roles, tenure, setTenure, sort, setSort, exportXlsx, shown, total: data.total }}
        filtered={groups.flatMap((g) => g.people)} />
      {groups.map((g) => <MbtiGroup key={g.mbti} group={g} color={color} />)}
    </div>
  );
}

const DAY = 86400000;
const TENURE = {
  "6m": (d) => Date.now() - d < 183 * DAY,
  "1y": (d) => Date.now() - d < 365 * DAY,
  "1y+": (d) => Date.now() - d >= 365 * DAY,
  "3y+": (d) => Date.now() - d >= 3 * 365 * DAY,
};
const yearsAt = (m) => {
  if (!m.joined_at) return 0;
  const j = new Date(m.joined_at), n = new Date();
  let y = n.getFullYear() - j.getFullYear();
  if (n.getMonth() < j.getMonth() || (n.getMonth() === j.getMonth() && n.getDate() < j.getDate())) y -= 1;
  return Math.max(0, y);
};
const TIERS = [
  { min: 10, color: "#E50000", label: "Lenda" },
  { min: 5, color: "#EAB308", label: "Ouro" },
  { min: 3, color: "#CBD5E1", label: "Prata" },
  { min: 1, color: "#CD7F32", label: "Bronze" },
];

function SeniorityBadge({ m }) {
  const y = yearsAt(m);
  const t = TIERS.find((x) => y >= x.min);
  if (!t) return null;
  const legend = y >= 10;
  return (
    <span title={`${t.label} · na KW Lead desde ${m.joined_at}`}
      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${legend ? "text-white" : "border"}`}
      style={legend ? { background: t.color, boxShadow: "0 0 12px rgba(229,0,0,.5)" } : { borderColor: t.color, color: t.color }}
      data-testid="club-member-seniority">
      {legend ? <Crown className="w-3 h-3" /> : <Award className="w-3 h-3" />}
      {y} {y === 1 ? "ano" : "anos"}{legend ? " · Lenda" : ""}
    </span>
  );
}
const joinedMs = (m) => (m.joined_at ? Date.parse(m.joined_at) : NaN);

function matches(m, q, role, tenure) {
  const t = q.trim().toLowerCase();
  if (t && !`${m.name || ""} ${m.email || ""} ${m.phone || ""}`.toLowerCase().includes(t)) return false;
  if (role && m.role !== role) return false;
  if (tenure) { const d = joinedMs(m); if (Number.isNaN(d) || !TENURE[tenure](d)) return false; }
  return true;
}

function sortPeople(list, sort) {
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
  const arr = [...list];
  if (sort === "name") return arr.sort(byName);
  if (sort === "oldest" || sort === "newest") {
    const dir = sort === "oldest" ? 1 : -1;
    return arr.sort((a, b) => {
      const da = joinedMs(a), db = joinedMs(b);
      if (Number.isNaN(da) !== Number.isNaN(db)) return Number.isNaN(da) ? 1 : -1;
      return (dir * (da - db)) || byName(a, b);
    });
  }
  return arr.sort((a, b) => (b.points ?? -1) - (a.points ?? -1) || byName(a, b));
}

function Toolbar({ q, setQ, role, setRole, roles, tenure, setTenure, sort, setSort, exportXlsx, shown, total, filtered }) {
  return (
    <div className="kw-card p-4 flex flex-wrap items-center gap-3" data-testid="club-members-toolbar">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2" />
        <input className="kw-input !pl-9 !py-2 w-full" placeholder="Pesquisar nome, email ou telefone…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="club-members-search" />
      </div>
      <select className="kw-input !py-2 !w-44 text-xs" value={role} onChange={(e) => setRole(e.target.value)}
        data-testid="club-members-role-filter">
        <option value="">Todos os cargos</option>
        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <select className="kw-input !py-2 !w-48 text-xs" value={tenure} onChange={(e) => setTenure(e.target.value)}
        data-testid="club-members-tenure-filter">
        <option value="">Qualquer antiguidade</option>
        <option value="6m">Entrou nos últimos 6 meses</option>
        <option value="1y">Entrou no último ano</option>
        <option value="1y+">Há mais de 1 ano</option>
        <option value="3y+">Há mais de 3 anos</option>
      </select>
      <select className="kw-input !py-2 !w-48 text-xs" value={sort} onChange={(e) => setSort(e.target.value)}
        data-testid="club-members-sort">
        <option value="points">Ordenar: pontos</option>
        <option value="name">Ordenar: nome</option>
        <option value="oldest">Antiguidade: mais antigos</option>
        <option value="newest">Antiguidade: mais recentes</option>
      </select>
      <span className="text-[10px] uppercase tracking-widest text-[#737373]" data-testid="club-members-shown">
        {shown} de {total}
      </span>
      <button onClick={exportXlsx}
        className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors"
        data-testid="club-members-export">
        <Download className="w-3.5 h-3.5" /> Excel
      </button>
      <ClubWhatsApp people={filtered} />
    </div>
  );
}

function MbtiGroup({ group, color }) {
  return (
    <section data-testid={`club-group-${group.mbti}`} className={group.people.length ? "" : "opacity-40"}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border w-14 text-center"
          style={{ borderColor: color, color }}>{group.mbti}</span>
        <span className="font-display font-black uppercase">{group.label}</span>
        <span className="text-xs text-[#737373]">{group.people.length}</span>
      </div>
      {group.people.length === 0 ? (
        <div className="text-xs text-[#555]">Ninguém com este perfil.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {group.people.map((m) => <MemberCard key={m.id} m={m} />)}
        </div>
      )}
    </section>
  );
}

function MemberCard({ m }) {
  const rc = ROLE_COLOR[m.role] || "#A3A3A3";
  const wa = whatsappLink(m.phone);
  return (
    <div className="kw-card p-4 flex gap-3 hover:border-[#444] transition-colors" data-testid={`club-member-${m.id}`}>
      <Avatar photoUrl={m.photo_url} name={m.name} size={52} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="font-bold truncate" data-testid="club-member-name">{m.name}</div>
        <span className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border"
          style={{ borderColor: rc, color: rc }} data-testid="club-member-role">{m.role}</span>
        {" "}<SeniorityBadge m={m} />
        {m.points != null && (
          <div className="text-xs text-[#A3A3A3] flex items-center gap-1" data-testid="club-member-points">
            <Trophy className="w-3 h-3 text-[#EAB308]" />
            <b className="text-white">{m.points}</b> pts{m.rank ? ` · #${m.rank} no clube` : ""}
          </div>
        )}
        {m.email && (
          <a href={`mailto:${m.email}`} className="text-[11px] text-[#A3A3A3] hover:text-white flex items-center gap-1 truncate"
            data-testid="club-member-email"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{m.email}</span></a>
        )}
        {m.phone && (
          <a href={`tel:${m.phone}`} className="text-[11px] text-[#A3A3A3] hover:text-white flex items-center gap-1"
            data-testid="club-member-phone"><Phone className="w-3 h-3" />{m.phone}</a>
        )}
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#25D366]/10 border border-[#25D366]/50 text-[#25D366] text-[10px] uppercase tracking-widest font-bold hover:bg-[#25D366]/20 transition-colors"
            data-testid="club-member-whatsapp"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
        )}
      </div>
    </div>
  );
}
